import { db, newId, type Room } from './schema';
import { forgetSizes, storeImage } from './images';

export type RoomDraft = {
  name: string;
  icon: string;
  /** A newly picked file. Encoding happens before any transaction opens. */
  cover?: Blob | undefined;
};

export async function createRoom(draft: RoomDraft): Promise<string> {
  const coverImageId = draft.cover === undefined ? undefined : await storeImage(draft.cover);

  const room: Room = {
    id: newId(),
    name: draft.name.trim(),
    icon: draft.icon,
    coverImageId,
    sort: await nextSort(),
    createdAt: Date.now(),
  };

  await db.rooms.add(room);
  return room.id;
}

export type RoomEdit = {
  name: string;
  icon: string;
  /** A replacement file, `null` to clear the cover, `undefined` to leave it. */
  cover?: Blob | null | undefined;
};

export async function updateRoom(id: string, edit: RoomEdit): Promise<void> {
  // Outside the transaction: canvas encoding is slow enough to time one out,
  // and Dexie transactions can't await promises that aren't theirs.
  const newCoverId = edit.cover instanceof Blob ? await storeImage(edit.cover) : undefined;

  await db.transaction('rw', db.rooms, db.images, async () => {
    const room = await db.rooms.get(id);
    if (room === undefined) return;

    const changes: Partial<Room> = {
      name: edit.name.trim(),
      icon: edit.icon,
    };

    if (edit.cover !== undefined) {
      changes.coverImageId = newCoverId;
      if (room.coverImageId !== undefined) {
        await db.images.delete(room.coverImageId);
      }
    }

    await db.rooms.update(id, changes);
  });
}

/**
 * Rooms, their items and every image either owns, in one transaction across
 * all three tables. Orphaned images are a real risk once stage 2 exists, and
 * writing this as three separate deletes now means patching it later.
 */
export async function deleteRoom(id: string): Promise<void> {
  const gone = await db.transaction('rw', db.rooms, db.items, db.images, async () => {
    const room = await db.rooms.get(id);
    const items = await db.items.where('roomId').equals(id).toArray();

    const imageIds = items.flatMap((item) => [
      ...(item.coverImageId === undefined ? [] : [item.coverImageId]),
      ...item.imageIds,
    ]);
    if (room?.coverImageId !== undefined) imageIds.push(room.coverImageId);

    await db.images.bulkDelete(imageIds);
    await db.items.bulkDelete(items.map((item) => item.id));
    await db.rooms.delete(id);
    return imageIds;
  });

  // Outside the transaction: it's a memory cache, and dropping it early would
  // be wrong if the write rolled back.
  forgetSizes(gone);
}

/**
 * Rewrites every room's sort to its index. With a house's worth of rooms the
 * cost is nil, and it keeps the values from drifting into fractions that
 * eventually collide.
 */
export async function reorderRooms(orderedIds: readonly string[]): Promise<void> {
  await db.transaction('rw', db.rooms, async () => {
    await Promise.all(orderedIds.map((id, index) => db.rooms.update(id, { sort: index })));
  });
}

export function listRooms(): Promise<Room[]> {
  return db.rooms.orderBy('sort').toArray();
}

export function countItems(roomId: string): Promise<number> {
  return db.items.where('roomId').equals(roomId).count();
}

async function nextSort(): Promise<number> {
  const last = await db.rooms.orderBy('sort').last();
  return last === undefined ? 0 : last.sort + 1;
}
